import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ConversationCard from './ConversationCard';
import CardErrorBoundary from './CardErrorBoundary';
import { Message } from '../types';
import api, { getMessages, getMessage, getCurrentUser, getSettings, isManager, showToast } from '../services/api';
import { pushNotification } from '../services/notifications';

// 'newUnowned' is the DEFAULT view (status=NEW + unowned) — the triage queue.
// 'all' loads every thread with NO server status filter, so a worked thread shows
// with its FULL message set (inbound AND outbound). A message-level status filter
// (like newUnowned) can't show outbound replies because those rows aren't
// status=NEW — so 'all' is how you see worked sub-threads + outbound.
// 'history' is managers/admin-only (status=COMPLETED). A thread that needs a
// human's attention carries the needs_human_review marker on the thread itself
// (shown/cleared on the card) — there is no separate review queue/tab.
// (Phase 3: 8x8 calls are consolidated INTO this unified feed as channel='CALL'
// message rows — the separate Phase 2 transcripts tab is gone.)
type FilterType = 'newUnowned' | 'inProgress' | 'escalated' | 'history';

// How many THREADS (root messages) each tab loads at once. The backend paginates
// the thread-aware path at the ROOT level (thread_limit = min(limit, 300), with
// OFFSET/FETCH over the qualifying roots), then expands each root into its full
// inbound+outbound message set. So passing limit=PAGE_SIZE caps the feed at
// PAGE_SIZE threads per page — which is what keeps the (heavy) History tab from
// trying to load hundreds of completed threads (+ their full expansions) at once.
// The user pages through PAGE_SIZE at a time via the Prev/Next control.
const PAGE_SIZE = 50;

export const MessageFeed: React.FC<{ lastMessage: Message | null }> = ({ lastMessage }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [filter, setFilter] = useState<FilterType>('newUnowned');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Thread-level pagination. `page` is 0-based; the offset sent to the server is
  // page * PAGE_SIZE ROOTS. `hasMore` is derived from the response: the server
  // returns exactly the roots it selected (capped at PAGE_SIZE), so if the
  // response contains PAGE_SIZE distinct roots there may be another page. Reset
  // to 0 whenever the tab or search changes (see the tab/search handlers).
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  // The single OPEN thread (its root/thread id), lifted here from the cards so the
  // open thread renders in a modal overlay (ThreadModal) instead of expanding
  // inline in the feed list. Exactly one thread is open at a time; null = none.
  // Because the modal is portal-mounted and keyed on this id, a background
  // poll/re-sort of the feed can reorder the list rows underneath WITHOUT
  // unmounting, moving, or closing the open thread.
  const [openThreadId, setOpenThreadId] = useState<number | null>(null);
  // Server-side free-text search (phone/name/message/thread-id, via Dev-C).
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // SignalR for real-time updates (instant path; the delta poll is the backbone).
  // The connection is owned by App/MainApp (single connection, and its state drives
  // the header LIVE pill); lastMessage arrives here via props.

  // Whether THIS user should get "new inbound" notification pings. Driven by the
  // newMessageNotify policy (manager-dispatch by default): managers/admins get
  // them; reps don't (they still see unclaimed threads in the feed + their own
  // assignments). 'all' = everyone. Defaults closed until settings resolve.
  const notifyNewRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    getSettings()
      .then((s) => { if (!cancelled) notifyNewRef.current = s?.newMessageNotify === 'all' || isManager(); })
      .catch(() => { if (!cancelled) notifyNewRef.current = isManager(); });
    return () => { cancelled = true; };
  }, []);

  // Watermark for incremental fetches: the newest updated_at we've seen.
  const lastSyncRef = useRef<string>('1970-01-01T00:00:00');
  const maxUpdated = (list: Message[]) =>
    list.reduce((mx, m) => {
      const u = m.updated_at || m.received_at || '';
      return u > mx ? u : mx;
    }, lastSyncRef.current);

  // Merge rows in by id (insert new, replace existing) without dropping the rest.
  const upsert = useCallback((incoming: Message[]) => {
    if (!incoming.length) return;
    setMessages(prev => {
      const map = new Map(prev.map(m => [m.id, m]));
      for (const m of incoming) map.set(m.id, m);
      return Array.from(map.values());
    });
  }, []);

  // Full load: initial render, filter change, and Refresh. Replaces the list.
  // latestReq guards against out-of-order async results: every call takes a
  // ticket; only the MOST RECENT call may commit its data. So when the filter
  // changes (or any reload starts), an older in-flight load — e.g. the previous
  // UNFILTERED fetch from the poll/SignalR — is discarded on arrival instead of
  // overwriting the new (filtered) view. That bouncing was the flicker.
  const latestReq = useRef(0);
  const fullLoad = useCallback(async (silent = false) => {
    const reqId = ++latestReq.current;
    try {
      if (!silent) setLoading(true);
      setError(null);
      const params: Record<string, string> = {};
      if (filter === 'newUnowned') { params.status = 'NEW'; params.unowned = 'true'; }  // default view: new + unclaimed
      if (filter === 'inProgress') params.status = 'IN_PROGRESS'; // everyone: claimed/active threads
      if (filter === 'escalated') params.status = 'ESCALATED';    // everyone: threads escalated to a human
      if (filter === 'history') params.status = 'COMPLETED'; // managers/admin: completed archive (server-enforced)
      if (search) params.search = search;   // free-text server-side search (Dev-C)
      // Thread-level pagination: PAGE_SIZE roots per page. offset is in ROOTS
      // (the server's thread-aware path applies OFFSET/FETCH to the root ids).
      params.limit = String(PAGE_SIZE);
      params.offset = String(page * PAGE_SIZE);
      const data = await getMessages(params);
      // Drop this result if a newer load has since started (stale params, e.g. the
      // pre-filter unfiltered fetch resolving after the filtered one began).
      if (reqId !== latestReq.current) return;
      // Derive hasMore from the DISTINCT roots in this page's response. The server
      // returns every message of the (up to PAGE_SIZE) roots it selected, so the
      // count of distinct COALESCE(thread_id, id) equals the roots on this page.
      // Exactly PAGE_SIZE roots ⇒ there may be another page; fewer ⇒ last page.
      const rootCount = new Set(data.map(m => m.thread_id ?? m.id)).size;
      setHasMore(rootCount >= PAGE_SIZE);
      // Skip the state update when the polled data is identical to what's shown
      // (same ids + same updated_at). Returning the SAME array makes React bail out
      // of the re-render, so an idle feed never re-renders or reorders.
      setMessages(prev => {
        const sig = (arr: Message[]) => arr.map(m => m.id + ':' + (m.updated_at || '')).sort().join('|');
        return sig(prev) === sig(data) ? prev : data;
      });
      lastSyncRef.current = maxUpdated(data);
    } catch (err) {
      if (reqId !== latestReq.current) return;  // stale failure — ignore
      if (!silent) setError('Failed to load messages');
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, search, page]);

  // Silent reload of the CURRENT filtered view — used by the poll, window focus,
  // and after-send. This REPLACES the old delta-upsert: that fetched changed-since
  // rows and upserted them, but those rows ignored the active filter, so off-filter
  // threads appeared then vanished on the next reload — the thread "flicker". A
  // plain filtered reload + the identity-skip in fullLoad keeps the view stable.
  const deltaRefresh = useCallback(async () => {
    return fullLoad(true);
  }, [fullLoad]);

  // Debounced reload for bursty SignalR pushes — coalesce a burst into one reload.
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleReload = useCallback(() => {
    if (reloadTimer.current) clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(() => { fullLoad(true); }, 700);
  }, [fullLoad]);
  // Hold the latest scheduleReload in a ref so the SignalR effect can call it
  // WITHOUT listing it as a dependency — otherwise the effect re-runs (and fires
  // an extra reload) every time the filter changes (which changes scheduleReload).
  const scheduleReloadRef = useRef(scheduleReload);
  scheduleReloadRef.current = scheduleReload;

  useEffect(() => { fullLoad(); }, [fullLoad]);

  // Debounce the search box: commit the input into `search` 350ms after typing
  // stops. fullLoad depends on `search`, so committing reloads automatically;
  // clearing the box (empty string) reloads the unfiltered tab too.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const id = setInterval(() => deltaRefresh(), 4000);  // poll backbone: 4s so new texts show fast even when SignalR is unavailable (Free-tier cap)
    const onFocus = () => deltaRefresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [deltaRefresh]);

  // SignalR push: fire notifications, then trigger a DEBOUNCED reload of the
  // current filtered view. We deliberately do NOT inject the pushed row into the
  // list — injecting rows that don't match the active filter made threads flicker
  // in and out. The reload respects the filter, so the view stays consistent.
  useEffect(() => {
    if (!lastMessage) return;
    // Turn live events into notifications (deduped by key in the store).
    // "New inbound" only pings for unclaimed threads — once someone owns it,
    // it stops notifying the whole team; assigned/escalated stay targeted.
    const m = lastMessage;
    const who = m.customer_name || m.sender_id;
    const threadKey = m.thread_id ?? m.id;
    if (m.direction !== 'outbound' && !m.assigned_to && notifyNewRef.current) {
      pushNotification({ key: `new-${m.id}`, kind: 'new', title: `New ${m.channel} from ${who}`, senderId: m.sender_id, threadKey });
    }
    if (m.status === 'ESCALATED') {
      pushNotification({ key: `esc-${m.id}`, kind: 'escalated', title: `Escalated: ${who}`, senderId: m.sender_id, threadKey });
    }
    // Flagged for human review — ping like escalated/assigned. Deduped per id.
    if (m.needs_human_review) {
      pushNotification({ key: `rev-${m.id}`, kind: 'review', title: `Needs review: ${who}`, senderId: m.sender_id, threadKey });
    }
    const me = getCurrentUser()?.name;
    if (me && m.assigned_to === me) {
      pushNotification({ key: `asg-${m.id}-${me}`, kind: 'assigned', title: `Assigned to you: ${who}`, senderId: m.sender_id, threadKey });
    }
    scheduleReloadRef.current();
    // Only react to a NEW push — not to scheduleReload's identity changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  // useCallback so these props are STABLE — together with the memoized
  // ConversationCard, an unchanged card never re-renders when the feed re-renders
  // for unrelated reasons, preventing reflow.
  // Use the axios `api` client (not raw fetch) so the Authorization bearer token
  // set by setAuthToken is attached and the backend can verify identity.
  //
  // FULLY OPTIMISTIC: the status-change PATCH then upserts the row the server
  // returns — and does NOT trigger any feed reload. The single upserted row
  // updates the card instantly with zero flicker; the 10s poll (deltaRefresh)
  // reconciles anything else within the interval. Do NOT re-add a reload here:
  // the redundant full reload after a mutation that already returned the updated
  // row was the ~5s "click then wait" lag. The upsert is sufficient.
  const handleStatusChange = useCallback(async (id: number, status: string) => {
    try {
      const res = await api.patch('/messages/' + id, { status });
      upsert([res.data]);   // optimistic single-row update — no reload (poll reconciles)
    } catch (err) {
      console.error('Failed to update status:', err);
      showToast('Could not update status — please try again.');
    }
  }, [upsert]);

  // onAfterSend: passed to ConversationCard. Used by actions that create NEW rows
  // the optimistic single-row upsert can't synthesize client-side — primarily a
  // REPLY send (must show the outbound), plus suggested-reply /
  // customer-created. It is the SILENT, filtered reload (fullLoad(true)):
  //   - silent: never flips `loading`, so the feed never blanks (cards stay put);
  //   - filtered: reloads the CURRENT view, so it can't drag in off-filter rows
  //     (that off-filter injection was the old delta-upsert flicker);
  //   - identity-skipped: fullLoad returns the same array when ids+updated_at are
  //     unchanged, so React bails out and nothing reorders.
  // Flag-review also routes through here (ConversationCard, not this file, owns
  // that wiring). See report note: making flag-review's BADGE optimistic would
  // need a ConversationCard prop, which is out of this file's ownership.
  const onAfterSend = useCallback(() => deltaRefresh(), [deltaRefresh]);

  // ── Keep the OPEN thread alive + follow it across tabs ─────────────────────
  // The modal must stay open the WHOLE time it's open — even when a status change
  // moves the thread to another tab (where the tab-filtered reload would drop it
  // and unmount the card + modal). Two mechanisms:
  //  (a) PIN — retain the open thread's last-seen messages and union them back into
  //      the render set whenever a reload drops them, so its card never unmounts.
  //  (b) FOLLOW — poll the open thread root's LIVE status (tab-independent single
  //      GET) and, when it moves to a different status tab, switch the BACKGROUND
  //      to that tab so the conversation shows behind the modal (Michael's ask:
  //      work a New thread -> auto-advances to In Progress -> background flips to
  //      the In Progress tab while the modal stays put on top).
  const pinnedOpenRef = useRef<Message[]>([]);
  useEffect(() => {
    if (openThreadId == null) { pinnedOpenRef.current = []; return; }
    const mine = messages.filter(m => (m.thread_id ?? m.id) === openThreadId);
    if (mine.length) pinnedOpenRef.current = mine;
  }, [messages, openThreadId]);

  const [openRootStatus, setOpenRootStatus] = useState<string | null>(null);
  useEffect(() => {
    if (openThreadId == null) { setOpenRootStatus(null); return; }
    let alive = true;
    const check = async () => {
      try { const m = await getMessage(openThreadId); if (alive && m) setOpenRootStatus(m.status ?? null); }
      catch { /* non-fatal — pin still keeps the modal open */ }
    };
    check();
    const id = setInterval(check, 3000);
    return () => { alive = false; clearInterval(id); };
  }, [openThreadId]);

  // Read the CURRENT filter via a ref so the guard below isn't a stale closure
  // (it used to capture an outdated `filter`) — WITHOUT adding `filter` to the
  // deps, which would make a manual tab switch re-fire this effect and yank the
  // user back to the open thread's tab. We only want to follow when the thread's
  // STATUS changes, not when the user changes tabs.
  const filterRef = useRef(filter);
  filterRef.current = filter;
  useEffect(() => {
    if (openThreadId == null || !openRootStatus) return;
    const map: Record<string, FilterType> = {
      IN_PROGRESS: 'inProgress', ESCALATED: 'escalated', COMPLETED: 'history', NEW: 'newUnowned',
    };
    const target = map[openRootStatus];
    if (!target || target === filterRef.current) return;
    if ((target === 'history' || target === 'escalated') && !isManager()) return;  // rep has no History/Escalated tab; the pin keeps it open
    setFilter(target); setPage(0);   // move the background to the thread's new status tab (from its first page)
  }, [openRootStatus, openThreadId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Render set = the (tab-filtered) messages, plus the pinned open-thread rows when
  // a reload has dropped them (so the open conversation is always present/mounted).
  const renderMessages = useMemo(() => {
    if (openThreadId == null) return messages;
    if (messages.some(m => (m.thread_id ?? m.id) === openThreadId)) return messages;
    if (pinnedOpenRef.current.length === 0) return messages;
    const ids = new Set(messages.map(m => m.id));
    return [...messages, ...pinnedOpenRef.current.filter(m => !ids.has(m.id))];
  }, [messages, openThreadId]);

  // Group messages into one conversation per phone number (sender_id).
  // No time-based splitting — all messages from a number stay in a single
  // thread, so a question and its (possibly much later) reply read as one
  // conversation instead of separate streams.
  const conversations = useMemo(() => {
    // Sort newest first
    const sorted = [...renderMessages].sort(
      (a, b) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime()
    );

    const bySender = new Map<string, Message[]>();
    for (const msg of sorted) {
      if (!bySender.has(msg.sender_id)) bySender.set(msg.sender_id, []);
      bySender.get(msg.sender_id)!.push(msg);
    }

    // Sort conversations by most recent message
    return Array.from(bySender.values()).sort((a, b) => {
      const latestA = Math.max(...a.map(m => new Date(m.received_at).getTime()));
      const latestB = Math.max(...b.map(m => new Date(m.received_at).getTime()));
      return latestB - latestA;
    });
  }, [renderMessages]);

  // The status tabs: New -> In Progress -> Escalated -> History (Completed).
  //   - "New" (new + unowned) and "In Progress" (status=IN_PROGRESS) are visible
  //     to EVERYONE. In Progress is no longer manager-only: reps need to see
  //     active/claimed threads too.
  //   - "Escalated" (status=ESCALATED) and "History" (status=COMPLETED) stay
  //     managers/admin-only, and the backend now enforces History server-side as
  //     well (the UI gate is UX-only).
  //   - A thread needing a human's attention carries the needs_human_review
  //     marker on the card itself (no dedicated review tab).
  const filterButtons: { key: FilterType; label: string }[] = [
    { key: 'newUnowned', label: 'New' },
    { key: 'inProgress', label: 'In Progress' },
    ...(isManager()
      ? [
          { key: 'escalated' as FilterType, label: 'Escalated' },
          { key: 'history' as FilterType, label: 'History' },
        ]
      : []),
  ];

  // Folder-tab styling (shape only — original colors kept). Every tab is the SAME
  // fixed width (sized to the longest label), sitting SIDE-BY-SIDE on the baseline
  // (no overlap). The active tab drops its bottom border and dips 1px over the
  // baseline (-mb-px), matching the content color so it reads as the open folder /
  // an extension of the content below.
  const getButtonClass = (key: FilterType) => {
    const base =
      'relative w-32 flex-none text-center whitespace-nowrap px-3 py-2 text-sm rounded-t-lg ' +
      // Flush side-by-side: pull each tab 1px left so its border collapses with the
      // previous tab's (no gap, no doubled line). first:ml-0 keeps the first tab
      // flush to the left edge.
      'border border-b-0 border-gray-300 transition-colors -ml-px first:ml-0';
    if (filter === key) {
      // Active = the SAME color as the content window (bg-gray-50) with its bottom
      // line removed, so it reads as a seamless extension of the content below.
      return base + ' bg-gray-50 text-gray-900 font-semibold z-10 -mb-px';
    }
    // Inactive = slightly grayed-out / recessed.
    return base + ' bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700';
  };

  // Empty-state copy, specific to the active tab (and to a search). Tells the
  // user the queue is simply empty rather than implying something failed.
  const emptyMessage = (() => {
    if (search) return `No messages match “${search}”`;
    switch (filter) {
      case 'newUnowned': return 'No New Messages';
      case 'escalated': return 'No escalated threads';
      case 'inProgress': return 'No threads in progress';
      case 'history': return 'No completed messages';
      default: return 'No messages found';
    }
  })();

  return (
    // No overflow-hidden here — it would make this a scroll container and break
    // the sticky filter bar (which needs to stick against the page/body scroll).
    <div className="flex-1 flex flex-col">
      {/* Filter Bar — sticky just below the app header (h-16 = top-16) so the
          tabs, search and refresh control stay accessible while scrolling the
          message list. bg-white so message cards don't show through; z below the
          app header's z-50. */}
      <div className="bg-white px-3 sm:px-6 pt-3 sm:pt-4 sticky top-16 z-30">
        {/* One row: folder tabs (left, side-by-side on the baseline) plus the
            search / refresh controls (pushed right, lifted off the
            baseline). The row's bottom border is the header/content divider; the
            active tab breaks it and matches the content color to look continuous.
            On mobile the row scrolls horizontally (hidden scrollbar) so all tabs +
            controls stay reachable; on sm+ it's overflow-visible so the active
            tab's 1px merge isn't clipped and the row simply fits. */}
        <div className="flex items-end border-b border-gray-300 overflow-x-auto no-scrollbar">
          {filterButtons.map(btn => (
            <button
              key={btn.key}
              onClick={() => { setFilter(btn.key); setPage(0); }}
              className={getButtonClass(btn.key)}
            >
              {btn.label}
            </button>
          ))}

          {/* Controls, pushed right and lifted off the baseline (pb-2) so the
              inputs float above the divider instead of sitting on it. */}
          <div className="ml-auto flex items-center gap-2 sm:gap-3 pb-2 pl-3 flex-shrink-0">
            {/* Free-text search (phone/name/message/thread-id, server-side). */}
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search…"
              className="flex-shrink-0 w-40 sm:w-56 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />

            <button
              onClick={() => fullLoad()}
              className="flex-shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 rounded-lg transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Messages List — capped width and centered so cards don't stretch
          edge-to-edge on large screens */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6 bg-gray-50">
        <div className="w-full sm:w-[92%] lg:w-[88%] xl:w-[82%] 2xl:w-[75%] max-w-[1800px] mx-auto space-y-4">
          {/* Spinner/error/empty only when there's NOTHING to show. Once the feed
              has cards, they stay rendered through every reload — the feed never
              blanks (that blank-then-reappear was the flicker). */}
          {loading && messages.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-700"></div>
            </div>
          )}

          {error && messages.length === 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && messages.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              {emptyMessage}
            </div>
          )}

          {conversations.map((convoMessages) => (
            <div key={convoMessages[0].sender_id} id={'convo-' + convoMessages[0].sender_id}>
              <CardErrorBoundary label={convoMessages[0].customer_name || convoMessages[0].sender_id}>
                <ConversationCard
                  messages={convoMessages}
                  onStatusChange={handleStatusChange}
                  onAfterSend={onAfterSend}
                  openThreadId={openThreadId}
                  onOpenThread={setOpenThreadId}
                  showCompleted={filter === 'history'}
                />
              </CardErrorBoundary>
            </div>
          ))}

          {/* Thread pager — PAGE_SIZE threads at a time. Shown whenever the user
              is past the first page OR another page may exist. Prev is disabled on
              page 0; Next is disabled once the last page returned fewer than
              PAGE_SIZE roots. Kept out of the empty state (only render with cards
              present, or when already paged in). */}
          {(page > 0 || (hasMore && messages.length > 0)) && (
            <div className="flex items-center justify-center gap-4 pt-2 pb-4">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Newer
              </button>
              <span className="text-sm text-gray-500">
                Page {page + 1}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={!hasMore}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Older →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageFeed;
